/* eslint-disable complexity */
import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Pill from 'carbon-react/lib/components/pill';

import { ExtendedReportTemplateList } from '@futrli/views/processors/reports/reportTemplatesList';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { getReportingEditTemplateUrl, matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { ListingPageRowLayout } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-layout/listing-page-row-layout';
import { ListingPageRowMetricCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-metric-cell/listing-page-row-metric-cell';
import { ListingPageRowNameCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-name-cell/listing-page-row-name-cell';
import { ResourceAccessLabel } from '@futrli/predict/sharing/components/resource-access-label/resource-access-label';

import { ReportTemplateActionsPopover } from './report-template-actions-popover';

export const ROW_HEIGHT = 86;

interface ReportTemplateRowProps {
  template: ExtendedReportTemplateList;
  onDelete?: () => void;
  onPublish?: () => void;
  isResourceSharingEnabled?: boolean;
  isFutrliTemplate?: boolean;
}

const getStatusBadge = (
  template: ExtendedReportTemplateList,
  intl: ReturnType<typeof useIntl>,
  isResourceSharingEnabled: boolean,
  isFutrliTemplate: boolean
) => {
  if (isResourceSharingEnabled) {
    if (template.permission_info?.sharing_status === 'shared') {
      return (
        <Pill colorVariant="positive" size="S" fill>
          {intl.formatMessage({ id: 'common.shared', defaultMessage: 'Shared' })}
        </Pill>
      );
    }

    return (
      <Pill size="S" pillRole="status">
        {intl.formatMessage({ id: 'common.private', defaultMessage: 'Private' })}
      </Pill>
    );
  }

  return template.status === 'published' ? (
    <Pill colorVariant="positive" size="S" fill>
      {isFutrliTemplate
        ? intl.formatMessage({ id: 'common.live', defaultMessage: 'Live' })
        : intl.formatMessage({ id: 'common.published', defaultMessage: 'Published' })}
    </Pill>
  ) : (
    <Pill size="S" pillRole="status">
      {intl.formatMessage({ id: 'common.draft', defaultMessage: 'Draft' })}
    </Pill>
  );
};

export const ReportTemplateRow = ({
  template,
  onDelete,
  onPublish,
  isResourceSharingEnabled = false,
  isFutrliTemplate = false
}: ReportTemplateRowProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const formattedDate = intl.formatDate(template.created, { day: '2-digit', month: 'short', year: 'numeric' });

  const handleNameClick = useCallback(() => {
    navigate(getReportingEditTemplateUrl(workspaceId, organisationId, template.id, isConsolidation));
  }, [navigate, workspaceId, organisationId, template.id, isConsolidation]);

  const orientationLabel =
    template.orientation === 'landscape' ? (
      <FormattedMessage id="reporting.landscape" defaultMessage="Landscape" />
    ) : template.orientation === 'portrait' ? (
      <FormattedMessage id="reporting.portrait" defaultMessage="Portrait" />
    ) : (
      <FormattedMessage id="reporting.dashboard" defaultMessage="Dashboard" />
    );

  return (
    <ListingPageRowLayout
      nameColumn={
        <ListingPageRowNameCell name={template.name} onNameClick={handleNameClick} subtext={orientationLabel} />
      }
      metrics={
        <>
          <ListingPageRowMetricCell
            size="default"
            label={<FormattedMessage id="common.status" defaultMessage="Status" />}
            value={getStatusBadge(template, intl, isResourceSharingEnabled, isFutrliTemplate)}
          />
          {isResourceSharingEnabled && (
            <ListingPageRowMetricCell
              size="default"
              label={<FormattedMessage id="common.access" defaultMessage="Access" />}
              value={<ResourceAccessLabel permission={template.permission_info?.effective_permission} />}
            />
          )}
          <ListingPageRowMetricCell
            size="pages"
            label={<FormattedMessage id="predict.pages" defaultMessage="Pages" />}
            value={template.page_count}
          />
          <ListingPageRowMetricCell
            size="default"
            label={<FormattedMessage id="predict.date_created" defaultMessage="Date created" />}
            value={formattedDate}
          />
          {isResourceSharingEnabled && (
            <ListingPageRowMetricCell
              size="wide"
              label={<FormattedMessage id="common.owner" defaultMessage="Owner" />}
              value={
                template.isOwner
                  ? intl.formatMessage({ id: 'common.you', defaultMessage: 'You' })
                  : template.ownerName || '—'
              }
            />
          )}
        </>
      }
      actions={<ReportTemplateActionsPopover template={template} onDelete={onDelete} onPublish={onPublish} />}
    />
  );
};
